import React, { useState, useMemo, useEffect } from 'react';
import moment from 'moment';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { Table, Input, Button } from 'reactstrap';

import { formatNumber, formatMs, formatBytes } from '../helpers/format';
import { statusClassOf, STATUS_CLASSES, CHART_INTERVALS, isIntervalAllowed, formatInTz } from '../helpers/apiLogs';

/**
* The three analytics tabs of the "API analytics (all requests)" page -
* Overview, Routes and Who calls us. All three render the same
* `/data/api-logs/overview` response, which the page fetches once.
*
* Color: requests over time, the duration histogram and the heatmap are one
* measure each, so they use the single sequential blue (the heatmap as a
* light->dark ramp). Avg vs p95 is identity, categorical slots 1-2 in their
* fixed order. Status classes and the up/down deltas use the reserved status
* colors, always next to a label or an arrow. Error % gets its own small
* chart under the requests chart, on the same time axis - two measures of a
* different scale never share one chart with two y-axes.
*/

const palette = {
    surface: '#FFFFFF',
    border: '#E6EDF5',
    textPrimary: '#3A3F56',
    textSecondary: '#52514E',
    textMuted: '#818FA5',
    blue: '#2a78d6',
    orange: '#eb6834',
    good: '#0ca30c',
    warning: '#fab219',
    critical: '#d03b3b',
    neutral: '#B4B2A9',
};

const STATUS_CLASS_COLORS = { '2xx': palette.good, '3xx': palette.neutral, '4xx': palette.warning, '5xx': palette.critical };

const styles = {
    kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 },
    tile: { backgroundColor: palette.surface, borderRadius: 6, padding: '14px 16px', boxShadow: '0 10px 20px rgba(0,0,0,0.06)' },
    tileLabel: { fontSize: 11, fontWeight: 600, color: palette.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 },
    tileValue: { fontSize: 22, fontWeight: 600, color: palette.textPrimary, lineHeight: 1.1 },
    tileSub: { fontSize: 12, color: palette.textSecondary, marginTop: 4 },
    card: { backgroundColor: palette.surface, borderRadius: 6, padding: '16px 18px', boxShadow: '0 10px 20px rgba(0,0,0,0.06)', marginBottom: 20 },
    sectionTitle: { fontSize: 13, fontWeight: 700, color: palette.textPrimary, marginBottom: 10 },
    note: { fontSize: 12, color: palette.textMuted, marginTop: 6 },
    twoCol: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 },
    threeCol: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, alignItems: 'start' },
    heatmapGrid: { display: 'grid', gridTemplateColumns: '40px repeat(24, minmax(10px, 1fr))', gap: 2 },
    heatmapHourLabel: { fontSize: 10, color: palette.textMuted, textAlign: 'center' },
    heatmapDayLabel: { fontSize: 12, color: palette.textSecondary, display: 'flex', alignItems: 'center' },
    heatmapCell: { aspectRatio: '1', borderRadius: 2, minHeight: 10 },
    legendRow: { display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 10 },
    legendItem: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: palette.textSecondary },
    swatch: { width: 10, height: 10, borderRadius: 2, flexShrink: 0 },
    quickRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 12 },
    mono: { fontFamily: "'SFMono-Regular', Menlo, Monaco, Consolas, monospace", fontSize: 12 },
    emptyState: { textAlign: 'center', padding: '30px 20px', color: palette.textMuted, fontSize: 13 },
};

const isNum = (v) => v !== null && v !== undefined && !isNaN(Number(v));
const pct = (v, digits = 1) => (isNum(v) ? `${Number(v).toFixed(digits)}%` : '—');
const ms = (v) => (isNum(v) ? formatMs(v) : '—');
const fixed1 = (v) => (isNum(v) ? Number(v).toFixed(1) : '—');
const bytes = (v) => (isNum(v) ? formatBytes(v) : '—');

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const SEVERITY_STYLE = {
    warning: { backgroundColor: '#FFF4D6', color: '#7a5200' },
    critical: { backgroundColor: '#FDE8E8', color: '#a12b2b' },
};

/** cell highlight: p95 > 1 s / 3 s, error % > 5 / 20 */
const p95Severity = (v) => (!isNum(v) ? null : v > 3000 ? 'critical' : v > 1000 ? 'warning' : null);
const errorSeverity = (v) => (!isNum(v) ? null : v > 20 ? 'critical' : v > 5 ? 'warning' : null);
/** avg DB queries per request: > 20 / > 100 is a sign of N+1 queries */
const dbQueriesSeverity = (v) => (!isNum(v) ? null : v > 100 ? 'critical' : v > 20 ? 'warning' : null);

function Flagged({ severity, children }) {
    if (!severity) return <span>{children}</span>;
    return (
        <span style={{ ...SEVERITY_STYLE[severity], padding: '1px 6px', borderRadius: 8, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {severity === 'critical' ? '● ' : '▲ '}{children}
        </span>
    );
}

/**
* Signed change with an arrow. `badWhenUp` = an increase is bad (time,
* errors: red up / green down); otherwise neutral ink (request volume).
* `points` = percentage points (+1.2 pp) instead of a relative %.
*/
function Delta({ value, badWhenUp = false, points = false, lang }) {
    if (!isNum(value)) {
        return <span style={{ color: palette.textMuted }}>–</span>;
    }

    let n = Number(value);
    let text = `${n > 0 ? '+' : n < 0 ? '-' : ''}${Math.abs(n).toFixed(1)}${points ? ' pp' : '%'}`;

    if (n === 0) {
        return <span style={{ color: palette.textMuted }}>{text}</span>;
    }

    let color = badWhenUp ? (n > 0 ? palette.critical : palette.good) : palette.textSecondary;

    return (
        <span style={{ color, fontWeight: 600, whiteSpace: 'nowrap' }} title={'Compared with the previous period'.translate(lang)}>
            {n > 0 ? '▲' : '▼'} {text}
        </span>
    );
}

function KpiTile({ label, value, delta, sub }) {
    return (
        <div style={styles.tile}>
            <div style={styles.tileLabel}>{label}</div>
            <div style={styles.tileValue}>{value}</div>
            {delta || sub ? (
                <div style={styles.tileSub}>
                    {delta}
                    {delta && sub ? ' · ' : null}
                    {sub}
                </div>
            ) : null}
        </div>
    );
}

const durationLabel = (v) => {
    if (v < 1000) return `${v} ms`;
    let s = v / 1000;
    return `${Number.isInteger(s) ? s : s.toFixed(1)} s`;
};

const bucketLabel = (row) => {
    if (!isNum(row.toMs)) return `${durationLabel(row.fromMs)}+`;
    if (row.toMs <= 1000) return `${row.fromMs}–${row.toMs} ms`;
    return `${durationLabel(row.fromMs)}–${durationLabel(row.toMs)}`;
};

const axisTicks = { color: palette.textMuted, font: { size: 11 } };
const legendOptions = { position: 'top', align: 'start', labels: { usePointStyle: true, boxWidth: 8, color: palette.textSecondary, font: { size: 12 } } };

/**
* Timeseries points before `coverage.dataSince` have no summaries behind
* them (not "zero traffic"), so they are dropped and the page shows a note.
*/
const coveredTimeseries = (data) => {
    let rows = data.timeseries || [];
    let since = data.coverage && data.coverage.dataSince;
    if (!since) return rows;
    return rows.filter((p) => moment.utc(p.at).format('YYYY-MM-DD') >= since);
};

const timeLabel = (at, interval, count) => {
    if (interval === 'hour' || interval === 'minute') return moment(at).format('MM-DD HH:mm');
    return moment.utc(at).format(count > 120 ? 'YYYY-MM-DD' : 'MM-DD');
};

/**
* Index of the first point with DB / response tracking (the deploy that
* started logging every request in detail), or null when tracking was on
* for the whole period - only then is the jump in requests worth a marker.
*/
const trackingStartIndex = (rows) => {
    let idx = rows.findIndex((p) => (p.dbQueries || 0) > 0 || (p.responseBytes || 0) > 0);
    if (idx <= 0) return null;
    return rows.slice(0, idx).some((p) => (p.requests || 0) > 0) ? idx : null;
};

/** dashed vertical line at a category index, drawn without an annotation plugin */
const markerPlugin = (index, label) => ({
    id: 'trackingMarker',
    afterDatasetsDraw: (chart) => {
        if (index === null || index === undefined || !chart.scales.x) return;
        let x = chart.scales.x.getPixelForValue(index);
        let { top, bottom } = chart.chartArea;
        let ctx = chart.ctx;
        ctx.save();
        ctx.strokeStyle = palette.textSecondary;
        ctx.setLineDash([4, 4]);
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(x, top);
        ctx.lineTo(x, bottom);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = palette.textSecondary;
        ctx.font = '11px sans-serif';
        ctx.fillText(label, x + 4, top + 11);
        ctx.restore();
    },
});

function RequestsChart({ rows, interval, markerIndex, requestsOnly, lang }) {
    let labels = rows.map((p) => timeLabel(p.at, interval, rows.length));

    let requests = {
        labels,
        datasets: [{
            label: 'Requests'.translate(lang),
            data: rows.map((p) => p.requests || 0),
            backgroundColor: palette.blue,
            borderRadius: 2,
            maxBarThickness: 24,
        }],
    };

    let errors = {
        labels,
        datasets: [{
            label: 'Error %'.translate(lang),
            data: rows.map((p) => (isNum(p.errorPercent) ? p.errorPercent : 0)),
            borderColor: palette.critical,
            backgroundColor: 'rgba(208, 59, 59, 0.10)',
            fill: true,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.15,
        }],
    };

    let tooltipFooter = (items) => {
        let p = rows[items[0].dataIndex];
        return p ? `4xx: ${formatNumber(p.errors4xx)} · 5xx: ${formatNumber(p.errors5xx)}` : '';
    };

    return (
        <>
            <div style={{ height: 240 }}>
                <Bar
                    data={requests}
                    plugins={[markerPlugin(markerIndex, 'Detailed logging starts'.translate(lang))]}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'index', intersect: false },
                        plugins: { legend: { display: false }, tooltip: { callbacks: { footer: requestsOnly ? undefined : tooltipFooter } } },
                        scales: {
                            // the error chart under it carries the time labels, unless there is none
                            x: { grid: { display: false }, ticks: { ...axisTicks, maxRotation: 0, autoSkip: true, display: !!requestsOnly } },
                            y: { beginAtZero: true, grid: { color: palette.border }, ticks: { ...axisTicks, precision: 0 } },
                        },
                    }}
                />
            </div>
            {requestsOnly ? null : (
            <>
            <div style={{ ...styles.note, margin: '10px 0 4px 0', fontWeight: 600 }}>{'Error % (4xx + 5xx)'.translate(lang)}</div>
            <div style={{ height: 110 }}>
                <Line
                    data={errors}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'index', intersect: false },
                        plugins: {
                            legend: { display: false },
                            tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${pct(ctx.parsed.y)}`, footer: tooltipFooter } },
                        },
                        scales: {
                            x: { grid: { display: false }, ticks: { ...axisTicks, maxRotation: 0, autoSkip: true } },
                            y: { beginAtZero: true, grid: { color: palette.border }, ticks: { ...axisTicks, maxTicksLimit: 3, callback: (v) => `${v}%` } },
                        },
                    }}
                />
            </div>
            </>
            )}
        </>
    );
}

function SpeedChart({ rows, interval, lang }) {
    let data = {
        labels: rows.map((p) => timeLabel(p.at, interval, rows.length)),
        datasets: [
            {
                label: 'Avg'.translate(lang),
                data: rows.map((p) => (p.requests ? p.avgMs : null)),
                borderColor: palette.blue,
                backgroundColor: 'transparent',
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 4,
                tension: 0.15,
                spanGaps: true,
            },
            {
                label: 'p95',
                data: rows.map((p) => (p.requests ? p.p95Ms : null)),
                borderColor: palette.orange,
                backgroundColor: 'transparent',
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 4,
                tension: 0.15,
                spanGaps: true,
            },
        ],
    };

    return (
        <div style={{ height: 260 }}>
            <Line
                data={data}
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: { legend: legendOptions, tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ms(ctx.parsed.y)}` } } },
                    scales: {
                        x: { grid: { display: false }, ticks: { ...axisTicks, maxRotation: 0, autoSkip: true } },
                        y: { beginAtZero: true, grid: { color: palette.border }, ticks: { ...axisTicks, callback: (v) => durationLabel(v) } },
                    },
                }}
            />
        </div>
    );
}

/** DB queries per interval, and the response size on its own small chart under it (same time axis) */
function DbTrafficChart({ rows, interval, lang }) {
    let labels = rows.map((p) => timeLabel(p.at, interval, rows.length));

    return (
        <>
            <div style={{ height: 200 }}>
                <Bar
                    data={{ labels, datasets: [{ label: 'DB queries'.translate(lang), data: rows.map((p) => p.dbQueries || 0), backgroundColor: palette.blue, borderRadius: 2, maxBarThickness: 24 }] }}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'index', intersect: false },
                        plugins: {
                            legend: { display: false },
                            tooltip: { callbacks: { footer: (items) => { let p = rows[items[0].dataIndex]; return p ? `${'DB time'.translate(lang)}: ${isNum(p.dbTimeSeconds) ? formatMs(p.dbTimeSeconds * 1000) : '—'}` : ''; } } },
                        },
                        scales: {
                            x: { grid: { display: false }, ticks: { ...axisTicks, display: false } },
                            y: { beginAtZero: true, grid: { color: palette.border }, ticks: { ...axisTicks, precision: 0 } },
                        },
                    }}
                />
            </div>
            <div style={{ ...styles.note, margin: '10px 0 4px 0', fontWeight: 600 }}>{'Response size'.translate(lang)}</div>
            <div style={{ height: 120 }}>
                <Line
                    data={{
                        labels,
                        datasets: [{
                            label: 'Response size'.translate(lang),
                            data: rows.map((p) => p.responseBytes || 0),
                            borderColor: palette.blue,
                            backgroundColor: 'rgba(42, 120, 214, 0.12)',
                            fill: true,
                            borderWidth: 2,
                            pointRadius: 0,
                            pointHoverRadius: 4,
                            tension: 0.15,
                        }],
                    }}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'index', intersect: false },
                        plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatBytes(ctx.parsed.y)}` } } },
                        scales: {
                            x: { grid: { display: false }, ticks: { ...axisTicks, maxRotation: 0, autoSkip: true } },
                            y: { beginAtZero: true, grid: { color: palette.border }, ticks: { ...axisTicks, maxTicksLimit: 4, callback: (v) => formatBytes(v) } },
                        },
                    }}
                />
            </div>
        </>
    );
}

function DurationHistogram({ histogram, lang }) {
    let rows = histogram || [];
    let total = rows.reduce((sum, r) => sum + (r.requests || 0), 0);

    return (
        <div style={{ height: 240 }}>
            <Bar
                data={{
                    labels: rows.map(bucketLabel),
                    datasets: [{ label: 'Requests'.translate(lang), data: rows.map((r) => r.requests || 0), backgroundColor: palette.blue, borderRadius: { topLeft: 4, topRight: 4 }, borderSkipped: false }],
                }}
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: { callbacks: { label: (ctx) => `${formatNumber(ctx.parsed.y)} ${'requests'.translate(lang)}${total ? ` (${pct((ctx.parsed.y / total) * 100)})` : ''}` } },
                    },
                    scales: {
                        x: { grid: { display: false }, ticks: { ...axisTicks, maxRotation: 45, autoSkip: false } },
                        y: { beginAtZero: true, grid: { color: palette.border }, ticks: { ...axisTicks, precision: 0 } },
                    },
                }}
            />
        </div>
    );
}

function Heatmap({ heatmap, tz, lang }) {
    let cells = {};
    let max = 0;
    (heatmap || []).forEach((c) => {
        cells[`${c.weekday}-${c.hour}`] = c;
        if (c.requests > max) max = c.requests;
    });

    return (
        <div>
            <div style={styles.heatmapGrid}>
                <div />
                {Array.from({ length: 24 }).map((_, h) => <div key={h} style={styles.heatmapHourLabel}>{h}</div>)}
                {WEEKDAYS.map((day, idx) => (
                    <React.Fragment key={day}>
                        <div style={styles.heatmapDayLabel}>{day.translate(lang)}</div>
                        {Array.from({ length: 24 }).map((_, h) => {
                            let c = cells[`${idx + 1}-${h}`];
                            let ratio = c && max ? c.requests / max : 0;
                            return (
                                <div
                                    key={h}
                                    style={{ ...styles.heatmapCell, backgroundColor: ratio ? `rgba(42, 120, 214, ${Math.max(0.08, ratio)})` : palette.border }}
                                    title={`${day.translate(lang)} ${h}:00 — ${formatNumber(c ? c.requests : 0)} ${'requests'.translate(lang)}${c && c.requests ? `, ${'avg'.translate(lang)} ${ms(c.avgMs)}` : ''}`}
                                />
                            );
                        })}
                    </React.Fragment>
                ))}
            </div>
            <div style={styles.note}>{`${'Time zone'.translate(lang)}: ${tz || '-'}. ${'Darker = more requests.'.translate(lang)}`}</div>
        </div>
    );
}

function StatusBreakdown({ byStatus, lang }) {
    let rows = (byStatus || []).slice().sort((a, b) => Number(a.status) - Number(b.status));
    let classes = STATUS_CLASSES.map((cls) => ({
        cls,
        requests: rows.filter((r) => statusClassOf(r.status) === cls).reduce((sum, r) => sum + (r.requests || 0), 0),
    })).filter((c) => c.requests > 0);
    let total = classes.reduce((sum, c) => sum + c.requests, 0);

    if (!total) {
        return <div style={styles.emptyState}>{'No requests in the selected period'.translate(lang)}</div>;
    }

    return (
        <div>
            <div style={{ height: 180 }}>
                <Doughnut
                    data={{
                        labels: classes.map((c) => c.cls),
                        datasets: [{ data: classes.map((c) => c.requests), backgroundColor: classes.map((c) => STATUS_CLASS_COLORS[c.cls]), borderColor: palette.surface, borderWidth: 2 }],
                    }}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        cutout: '62%',
                        plugins: {
                            legend: { display: false },
                            tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${formatNumber(ctx.parsed)} (${pct((ctx.parsed / total) * 100)})` } },
                        },
                    }}
                />
            </div>
            <div style={styles.legendRow}>
                {classes.map((c) => (
                    <span key={c.cls} style={styles.legendItem}>
                        <span style={{ ...styles.swatch, backgroundColor: STATUS_CLASS_COLORS[c.cls] }} />
                        {c.cls}: {formatNumber(c.requests)} ({pct((c.requests / total) * 100)})
                    </span>
                ))}
            </div>
            <Table size="sm" hover style={{ fontSize: 13, marginTop: 12 }}>
                <thead><tr><th>{'Status'.translate(lang)}</th><th>{'Requests'.translate(lang)}</th><th>%</th></tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.status}>
                            <td>{r.status}</td>
                            <td>{formatNumber(r.requests)}</td>
                            <td>{pct((r.requests / total) * 100)}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
}

function MethodsChart({ byMethod, lang }) {
    let rows = (byMethod || []).slice().sort((a, b) => b.requests - a.requests);

    return (
        <div style={{ height: Math.max(120, rows.length * 34) }}>
            <Bar
                data={{ labels: rows.map((r) => r.method), datasets: [{ label: 'Requests'.translate(lang), data: rows.map((r) => r.requests), backgroundColor: palette.blue, borderRadius: 3, maxBarThickness: 20 }] }}
                options={{
                    indexAxis: 'y',
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                        x: { beginAtZero: true, grid: { color: palette.border }, ticks: { ...axisTicks, precision: 0 } },
                        y: { grid: { display: false }, ticks: axisTicks },
                    },
                }}
            />
        </div>
    );
}

const fmtRate = (v, digits) => (isNum(v) ? Number(v).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits }) : '—');

/** average load per unit, with the peak under it; a peak with a time opens the logs around it */
function RateTile({ label, avg, digits, peak, peakText, onOpenPeak, lang }) {
    let hasPeak = peak && (peak.at || peak.day) && isNum(peak.requests);
    return (
        <div style={styles.tile}>
            <div style={styles.tileLabel}>{label}</div>
            <div style={styles.tileValue}>{fmtRate(avg, digits)}</div>
            <div style={styles.tileSub}>
                {'Peak'.translate(lang)}:{' '}
                {!hasPeak ? '—' : onOpenPeak ? (
                    <button
                        type="button"
                        style={{ background: 'transparent', border: 'none', padding: 0, color: '#33405E', textDecoration: 'underline', cursor: 'pointer', fontSize: 12 }}
                        title={'Open the logs around this moment (±5 min)'.translate(lang)}
                        onClick={onOpenPeak}
                    >
                        {formatNumber(peak.requests)} {peakText}
                    </button>
                ) : <span>{formatNumber(peak.requests)} {peakText}</span>}
            </div>
        </div>
    );
}

function IntervalSwitch({ value, rangeDays, onChange, disabled, lang }) {
    return (
        <div className="table-tabs" style={{ marginBottom: 0 }}>
            {CHART_INTERVALS.map((i) => {
                let allowed = isIntervalAllowed(i.value, rangeDays);
                return (
                    <button
                        key={i.value}
                        className={value === i.value ? 'active' : ''}
                        disabled={!allowed || disabled}
                        style={!allowed ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                        title={!allowed ? `${'Available for periods up to'.translate(lang)} ${i.maxDays} ${(i.maxDays === 1 ? 'day' : 'days').translate(lang)}` : undefined}
                        onClick={() => allowed && value !== i.value && onChange(i.value)}
                    >
                        {i.label.translate(lang)}
                    </button>
                );
            })}
        </div>
    );
}

/**
* @param {number} rangeDays - length of the picked period, for which chart intervals are allowed
* @param {function} onChartInterval - called with 'minute' | 'hour' | 'day'
* @param {boolean} [chartLoading] - a new interval is loading, the data shown is still the previous one
* @param {function} onOpenLogsAround - called with an ISO time; the page opens the logs ±5 min around it
*/
export function ApiLogsOverviewTab({ data, rangeDays, onChartInterval, chartLoading, onOpenLogsAround, lang }) {
    let t = data.totals || {};
    let change = data.change || {};
    let rows = coveredTimeseries(data);
    let interval = data.interval;

    if (!t.requests) {
        return <div style={styles.emptyState}>{'No requests in the selected period'.translate(lang)}</div>;
    }

    let markerIndex = trackingStartIndex(rows);
    let trackedRows = markerIndex === null ? rows : rows.slice(markerIndex);
    let hasTracking = trackedRows.some((p) => (p.dbQueries || 0) > 0 || (p.responseBytes || 0) > 0);
    // only worth saying when part of the period is from before detailed logging
    let trackedSub = t.trackedRequests && t.trackedRequests < t.requests
        ? `${formatNumber(t.trackedRequests)} ${'of'.translate(lang)} ${formatNumber(t.requests)} ${'requests tracked'.translate(lang)}`
        : null;

    let deltaOf = (key, opts = {}) => (data.change ? <Delta value={change[key]} lang={lang} {...opts} /> : null);
    let rates = t.rates || {};
    let peaks = t.peaks || {};
    let isMinute = interval === 'minute';
    let openAround = (peak) => (peak && peak.at && onOpenLogsAround ? () => onOpenLogsAround(peak.at) : null);
    let intervalLabel = { minute: 'per minute', hour: 'per hour', day: 'per day' }[interval] || 'per day';

    return (
        <div>
            <div style={{ ...styles.sectionTitle, marginBottom: 8 }}>{'Load'.translate(lang)}</div>
            <div style={styles.kpiRow}>
                <RateTile label={'Req / second'.translate(lang)} avg={rates.avgPerSecond} digits={3} peak={peaks.perSecond}
                    peakText={`${'at'.translate(lang)} ${formatInTz(peaks.perSecond && peaks.perSecond.at, { seconds: true })}`} onOpenPeak={openAround(peaks.perSecond)} lang={lang} />
                <RateTile label={'Req / minute'.translate(lang)} avg={rates.avgPerMinute} digits={2} peak={peaks.perMinute}
                    peakText={`${'at'.translate(lang)} ${formatInTz(peaks.perMinute && peaks.perMinute.at)}`} onOpenPeak={openAround(peaks.perMinute)} lang={lang} />
                <RateTile label={'Req / hour'.translate(lang)} avg={rates.avgPerHour} digits={0} peak={peaks.perHour}
                    peakText={`${'at'.translate(lang)} ${formatInTz(peaks.perHour && peaks.perHour.at)}`} onOpenPeak={openAround(peaks.perHour)} lang={lang} />
                <RateTile label={'Req / day'.translate(lang)} avg={rates.avgPerDay} digits={0} peak={peaks.perDay}
                    peakText={`${'on'.translate(lang)} ${peaks.perDay && peaks.perDay.day ? peaks.perDay.day : '—'}`} lang={lang} />
            </div>
            <div style={{ ...styles.note, marginTop: -12, marginBottom: 16 }}>
                {'Averages cover only the part of the period that has data. Times in Europe/Stockholm.'.translate(lang)}
            </div>

            <div style={styles.kpiRow}>
                <KpiTile label={'Requests'.translate(lang)} value={formatNumber(t.requests)} delta={deltaOf('requestsPercent')} />
                <KpiTile label={'Avg per day'.translate(lang)} value={formatNumber(Math.round(t.avgRequestsPerDay || 0))} />
                <KpiTile
                    label={'Errors %'.translate(lang)}
                    value={pct(t.errorPercent)}
                    delta={deltaOf('errorPercentPoints', { badWhenUp: true, points: true })}
                    sub={`4xx ${formatNumber(t.errors4xx)} / 5xx ${formatNumber(t.errors5xx)}`}
                />
                <KpiTile label={'Avg time'.translate(lang)} value={ms(t.avgMs)} delta={deltaOf('avgMsPercent', { badWhenUp: true })} />
                <KpiTile label="p95" value={ms(t.p95Ms)} delta={deltaOf('p95MsPercent', { badWhenUp: true })} sub={`p99 ${ms(t.p99Ms)}`} />
                <KpiTile
                    label={'Slow %'.translate(lang)}
                    value={pct(t.slowPercent)}
                    sub={`${formatNumber(t.slowRequests)} ≥ ${durationLabel(data.slowThresholdMs || 1000)}`}
                />
                <KpiTile
                    label={'Avg DB queries / request'.translate(lang)}
                    value={fixed1(t.avgDbQueries)}
                    sub={trackedSub}
                />
                <KpiTile label={'Time in DB'.translate(lang)} value={pct(t.dbTimePercent)} sub={isNum(t.dbTimeSeconds) && t.trackedRequests ? formatMs(t.dbTimeSeconds * 1000) : null} />
                <KpiTile
                    label={'Response traffic'.translate(lang)}
                    value={t.trackedRequests ? bytes(t.responseBytes) : '—'}
                    sub={isNum(t.avgResponseBytes) ? `${'avg'.translate(lang)} ${formatBytes(t.avgResponseBytes)} / ${'request'.translate(lang)}` : null}
                />
                <KpiTile
                    label={'Busiest hour'.translate(lang)}
                    value={t.peakHour ? formatNumber(t.peakHour.requests) : '—'}
                    sub={t.peakHour && t.peakHour.at ? moment(t.peakHour.at).format('YYYY-MM-DD HH:mm') : null}
                />
            </div>

            <div style={styles.card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
                    <div style={{ ...styles.sectionTitle, marginBottom: 0 }}>
                        {`${'Requests over time'.translate(lang)} (${intervalLabel.translate(lang)})`}
                        {chartLoading ? <span style={{ fontWeight: 400, color: palette.textMuted }}> · {'Loading...'.translate(lang)}</span> : null}
                    </div>
                    {onChartInterval ? <IntervalSwitch value={interval} rangeDays={rangeDays} onChange={onChartInterval} disabled={chartLoading} lang={lang} /> : null}
                </div>
                {rows.length
                    ? <RequestsChart rows={rows} interval={interval} markerIndex={markerIndex} requestsOnly={isMinute} lang={lang} />
                    : <div style={styles.emptyState}>{'No data for this period yet.'.translate(lang)}</div>}
                {isMinute ? <div style={styles.note}>{'Per minute there are only request counts - errors and speed are on the hourly / daily chart.'.translate(lang)}</div> : null}
                {markerIndex !== null ? (
                    <div style={styles.note}>
                        {`${'Since'.translate(lang)} ${timeLabel(rows[markerIndex].at, interval, rows.length)} ${'every request is logged (not only some), so the jump in requests is not more traffic.'.translate(lang)}`}
                    </div>
                ) : null}
            </div>

            <div style={styles.card}>
                <div style={styles.sectionTitle}>{'Database and traffic over time'.translate(lang)}</div>
                {isMinute
                    ? <div style={styles.emptyState}>{'Not available per minute - switch the chart above to Hour or Day.'.translate(lang)}</div>
                    : hasTracking
                    ? <DbTrafficChart rows={trackedRows} interval={interval} lang={lang} />
                    : <div style={styles.emptyState}>{'No DB / response data in this period - only requests logged after the deploy have it.'.translate(lang)}</div>}
            </div>

            <div style={styles.card}>
                <div style={styles.sectionTitle}>{'Speed over time'.translate(lang)}</div>
                {isMinute
                    ? <div style={styles.emptyState}>{'Not available per minute - switch the chart above to Hour or Day.'.translate(lang)}</div>
                    : rows.length ? <SpeedChart rows={rows} interval={interval} lang={lang} /> : <div style={styles.emptyState}>{'No data for this period yet.'.translate(lang)}</div>}
                <div style={styles.note}>{'p50/p95/p99 are estimated from the duration histogram (accurate to a few tens of ms).'.translate(lang)}</div>
            </div>

            <div style={styles.twoCol}>
                <div style={styles.card}>
                    <div style={styles.sectionTitle}>{'Duration histogram'.translate(lang)}</div>
                    <DurationHistogram histogram={data.durationHistogram} lang={lang} />
                </div>
                <div style={styles.card}>
                    <div style={styles.sectionTitle}>{'When we are busiest'.translate(lang)}</div>
                    <Heatmap heatmap={data.heatmap} tz={data.tz} lang={lang} />
                </div>
            </div>

            <div style={styles.twoCol}>
                <div style={styles.card}>
                    <div style={styles.sectionTitle}>{'Statuses'.translate(lang)}</div>
                    <StatusBreakdown byStatus={data.byStatus} lang={lang} />
                </div>
                <div style={styles.card}>
                    <div style={styles.sectionTitle}>{'Methods'.translate(lang)}</div>
                    <MethodsChart byMethod={data.byMethod} lang={lang} />
                </div>
            </div>
        </div>
    );
}

const MIN_REQUESTS_FOR_ERRORS = 20;

const ROUTE_VIEWS = [
    { key: 'load', label: 'Heaviest', sortKey: 'timeSharePercent' },
    { key: 'slowest', label: 'Slowest', sortKey: 'p95Ms' },
    { key: 'errors', label: 'Most errors', sortKey: 'errorPercent', filter: (r) => (r.requests || 0) >= MIN_REQUESTS_FOR_ERRORS },
    { key: 'calls', label: 'Most calls', sortKey: 'requests' },
    { key: 'growth', label: 'Biggest growth', sortKey: 'requestsChangePercent' },
    { key: 'dbQueries', label: 'Most DB queries', sortKey: 'avgDbQueries' },
    { key: 'response', label: 'Biggest responses', sortKey: 'avgResponseBytes' },
];

const DB_TRAFFIC_HINT = 'Estimate: number of documents × average document size in the table';
const routeKey = (r) => `${r.method} ${r.route}`;

/** missing values (a route new in this period has no growth %, old logs have no DB data) always go last */
const sortRows = (list, key, dir = -1, text = false) => list.slice().sort((a, b) => {
    let va = a[key];
    let vb = b[key];
    if (text) return String(va || '').localeCompare(String(vb || '')) * dir;
    if (!isNum(va) || !isNum(vb)) return isNum(va) === isNum(vb) ? 0 : isNum(va) ? -1 : 1;
    return (Number(va) - Number(vb)) * dir;
});

/** "Tables it uses": the route's rows from `db.dependencies` */
function RouteTables({ deps, onOpenLogs, lang }) {
    return (
        <div style={{ padding: '6px 4px 12px 4px' }}>
            <div style={{ ...styles.sectionTitle, fontSize: 12 }}>{'Tables it uses'.translate(lang)}</div>
            {deps.length ? (
                <Table size="sm" style={{ fontSize: 12, backgroundColor: palette.surface, marginBottom: 10 }}>
                    <thead>
                        <tr>
                            <th>{'Table'.translate(lang)}</th>
                            <th>{'Queries / request'.translate(lang)}</th>
                            <th>{'Avg ms'.translate(lang)}</th>
                            <th>{'Total ms'.translate(lang)}</th>
                            <th>{'Docs'.translate(lang)}</th>
                            <th title={DB_TRAFFIC_HINT.translate(lang)}>{'Traffic (est.)'.translate(lang)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {deps.map((d) => (
                            <tr key={d.collection}>
                                <td style={styles.mono}>{d.collection}</td>
                                <td><Flagged severity={dbQueriesSeverity(d.avgQueriesPerRequest)}>{fixed1(d.avgQueriesPerRequest)}</Flagged></td>
                                <td>{ms(d.avgMs)}</td>
                                <td>{ms(d.totalMs)}</td>
                                <td>{formatNumber(d.docs)}</td>
                                <td>{bytes(d.estimatedBytes)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            ) : (
                <div style={{ ...styles.note, marginBottom: 10 }}>{'No DB data for this route in the period (only requests logged after the deploy have it).'.translate(lang)}</div>
            )}
            <Button size="sm" color="primary" onClick={onOpenLogs}>{'Logs (last 24 h, slowest first)'.translate(lang)}</Button>
        </div>
    );
}

/**
* @param {function} onOpenRoute - called with the route row from the "Logs" button; the page opens the Logs tab for it
* @param {object} [focus] - `{method, route, token}`: search for that route and open its panel (a link from the Database tab)
*/
export function ApiLogsRoutesTab({ data, onOpenRoute, focus, lang }) {
    let [view, setView] = useState('load');
    let [search, setSearch] = useState('');
    let [openKey, setOpenKey] = useState(null);

    useEffect(() => {
        if (!focus || !focus.route) return;
        setSearch(focus.route);
        setOpenKey(routeKey(focus));
    }, [focus]);

    let rows = useMemo(() => {
        let def = ROUTE_VIEWS.find((v) => v.key === view) || ROUTE_VIEWS[0];
        let needle = search.trim().toLowerCase();
        let list = (data.routes || []).filter((r) => (!def.filter || def.filter(r)) && (!needle || routeKey(r).toLowerCase().indexOf(needle) !== -1));
        return sortRows(list, def.sortKey);
    }, [data.routes, view, search]);

    let depsByRoute = useMemo(() => {
        let map = {};
        ((data.db && data.db.dependencies) || []).forEach((d) => { (map[routeKey(d)] = map[routeKey(d)] || []).push(d); });
        Object.keys(map).forEach((k) => { map[k] = sortRows(map[k], 'totalMs'); });
        return map;
    }, [data.db]);

    const COLS = 14;

    return (
        <div style={styles.card}>
            <div style={styles.quickRow}>
                <div className="table-tabs" style={{ marginBottom: 0 }}>
                    {ROUTE_VIEWS.map((v) => (
                        <button key={v.key} className={view === v.key ? 'active' : ''} onClick={() => setView(v.key)}>{v.label.translate(lang)}</button>
                    ))}
                </div>
                <Input type="text" style={{ height: 38, maxWidth: 260 }} placeholder={'Search routes...'.translate(lang)} value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div style={{ ...styles.note, marginTop: 0, marginBottom: 10 }}>
                {view === 'errors' ? `${'Only routes with at least'.translate(lang)} ${MIN_REQUESTS_FOR_ERRORS} ${'requests.'.translate(lang)} ` : ''}
                {'Parts like :id, :n, :param and :slug stand for the changing part of the URL (ID, number, SKU, slug). Click a route to see the tables it uses and its logs.'.translate(lang)}
            </div>

            <Table responsive hover size="sm" style={{ fontSize: 13 }}>
                <thead>
                    <tr>
                        <th>{'Method'.translate(lang)}</th>
                        <th>{'Route'.translate(lang)}</th>
                        <th>{'Requests'.translate(lang)}</th>
                        <th title={'Share of all requests'.translate(lang)}>{'Req. share'.translate(lang)}</th>
                        <th title={'Share of the total processing time'.translate(lang)}>{'Time share'.translate(lang)}</th>
                        <th>{'Avg'.translate(lang)}</th>
                        <th>p95</th>
                        <th>{'Max'.translate(lang)}</th>
                        <th>{'Slow %'.translate(lang)}</th>
                        <th>{'Errors %'.translate(lang)}</th>
                        <th title={'Average DB queries per request - over 20 is often an N+1 query'.translate(lang)}>{'Avg queries'.translate(lang)}</th>
                        <th title={'Share of the processing time spent in the database'.translate(lang)}>{'DB time %'.translate(lang)}</th>
                        <th title={'Average response size (before compression)'.translate(lang)}>{'Avg response'.translate(lang)}</th>
                        <th title={'Requests compared with the previous period'.translate(lang)}>{'Change'.translate(lang)}</th>
                    </tr>
                </thead>
                <tbody>
                    {!rows.length ? (
                        <tr><td colSpan={COLS} style={styles.emptyState}>{'No routes match.'.translate(lang)}</td></tr>
                    ) : rows.map((r, idx) => {
                        let key = routeKey(r);
                        let open = openKey === key;
                        return (
                            <React.Fragment key={`${key} ${idx}`}>
                                <tr style={{ cursor: 'pointer', backgroundColor: open ? '#F4F6F8' : undefined }} onClick={() => setOpenKey(open ? null : key)}>
                                    <td style={styles.mono}>{open ? '▾ ' : '▸ '}{r.method}</td>
                                    <td style={{ ...styles.mono, wordBreak: 'break-all' }}>{r.route}</td>
                                    <td>{formatNumber(r.requests)}</td>
                                    <td>{pct(r.requestSharePercent)}</td>
                                    <td>{pct(r.timeSharePercent)}</td>
                                    <td>{ms(r.avgMs)}</td>
                                    <td><Flagged severity={p95Severity(r.p95Ms)}>{ms(r.p95Ms)}</Flagged></td>
                                    <td>{ms(r.maxMs)}</td>
                                    <td>{pct(r.slowPercent)}</td>
                                    <td><Flagged severity={errorSeverity(r.errorPercent)}>{pct(r.errorPercent)}</Flagged></td>
                                    <td><Flagged severity={dbQueriesSeverity(r.avgDbQueries)}>{fixed1(r.avgDbQueries)}</Flagged></td>
                                    <td>{pct(r.dbTimePercent)}</td>
                                    <td>{bytes(r.avgResponseBytes)}</td>
                                    <td>
                                        {isNum(r.requestsChangePercent)
                                            ? <Delta value={r.requestsChangePercent} lang={lang} />
                                            : <span style={{ color: palette.textMuted }} title={`${'Previous period'.translate(lang)}: ${formatNumber(r.previousRequests)}`}>{r.previousRequests ? '–' : 'new'.translate(lang)}</span>}
                                    </td>
                                </tr>
                                {open ? (
                                    <tr style={{ backgroundColor: '#F4F6F8' }}>
                                        <td colSpan={COLS}>
                                            <RouteTables deps={depsByRoute[key] || []} onOpenLogs={() => onOpenRoute && onOpenRoute(r)} lang={lang} />
                                        </td>
                                    </tr>
                                ) : null}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </Table>
        </div>
    );
}

const COLLECTION_COLUMNS = [
    { key: 'collection', label: 'Table', text: true },
    { key: 'queries', label: 'Queries' },
    { key: 'totalMs', label: 'Total ms' },
    { key: 'avgMs', label: 'Avg ms' },
    { key: 'docs', label: 'Docs' },
    { key: 'docBytes', label: 'Avg doc size', title: 'Average document size in the table' },
    { key: 'estimatedBytes', label: 'Traffic (est.)', title: DB_TRAFFIC_HINT },
    { key: 'requests', label: 'Requests', title: 'Requests that used this table' },
    { key: 'routes', label: 'Routes', title: 'How many different routes use it' },
];

const MATRIX_SIZE = 15;

/**
* Route → table matrix: top routes (rows) × top tables (columns) by DB time,
* cell shade = DB time (one measure, so the sequential blue ramp).
*/
function DependencyMatrix({ dependencies, onOpenRoute, lang }) {
    let { routes, tables, cells, max } = useMemo(() => {
        let byRoute = {};
        let byTable = {};
        let cellMap = {};
        let maxMs = 0;
        (dependencies || []).forEach((d) => {
            let rk = routeKey(d);
            byRoute[rk] = byRoute[rk] || { method: d.method, route: d.route, totalMs: 0 };
            byRoute[rk].totalMs += d.totalMs || 0;
            byTable[d.collection] = (byTable[d.collection] || 0) + (d.totalMs || 0);
            cellMap[`${rk}|${d.collection}`] = d;
        });
        let topRoutes = Object.values(byRoute).sort((a, b) => b.totalMs - a.totalMs).slice(0, MATRIX_SIZE);
        let topTables = Object.keys(byTable).sort((a, b) => byTable[b] - byTable[a]).slice(0, MATRIX_SIZE);
        topRoutes.forEach((r) => topTables.forEach((t) => {
            let c = cellMap[`${routeKey(r)}|${t}`];
            if (c && c.totalMs > maxMs) maxMs = c.totalMs;
        }));
        return { routes: topRoutes, tables: topTables, cells: cellMap, max: maxMs };
    }, [dependencies]);

    if (!routes.length) return null;

    return (
        <div style={styles.card}>
            <div style={styles.sectionTitle}>{'Route → table'.translate(lang)}</div>
            <div style={{ ...styles.note, marginTop: 0, marginBottom: 10 }}>
                {`${'Top'.translate(lang)} ${MATRIX_SIZE} ${'routes × top'.translate(lang)} ${MATRIX_SIZE} ${'tables by time in the database. Darker = more DB time. Click a route to open it in Routes.'.translate(lang)}`}
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'separate', borderSpacing: 2, fontSize: 11 }}>
                    <thead>
                        <tr>
                            <th />
                            {tables.map((t) => (
                                <th key={t} style={{ ...styles.mono, fontSize: 10, fontWeight: 600, color: palette.textSecondary, writingMode: 'vertical-rl', transform: 'rotate(180deg)', padding: '4px 0', textAlign: 'left', maxHeight: 140 }}>{t}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {routes.map((r) => (
                            <tr key={routeKey(r)}>
                                <td style={{ ...styles.mono, fontSize: 11, paddingRight: 8, whiteSpace: 'nowrap', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }} title={routeKey(r)}>
                                    <button type="button" style={{ background: 'transparent', border: 'none', padding: 0, color: '#33405E', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => onOpenRoute && onOpenRoute(r)}>
                                        {routeKey(r)}
                                    </button>
                                </td>
                                {tables.map((t) => {
                                    let c = cells[`${routeKey(r)}|${t}`];
                                    let ratio = c && max ? (c.totalMs || 0) / max : 0;
                                    return (
                                        <td
                                            key={t}
                                            style={{ width: 22, height: 22, minWidth: 22, borderRadius: 2, backgroundColor: c ? `rgba(42, 120, 214, ${Math.max(0.08, ratio)})` : palette.border }}
                                            title={c ? `${routeKey(r)} → ${t}\n${'DB time'.translate(lang)}: ${ms(c.totalMs)}\n${'Queries / request'.translate(lang)}: ${fixed1(c.avgQueriesPerRequest)}\n${'Traffic (est.)'.translate(lang)}: ${bytes(c.estimatedBytes)}` : `${routeKey(r)} → ${t}: —`}
                                        />
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/**
* "Database" tab: which tables load the database most, who uses them and
* how much data we pull. Same `/overview` response (`db`).
*
* @param {function} onOpenRoute - called with `{method, route}`; the page opens it in the Routes tab
*/
export function ApiLogsDatabaseTab({ data, onOpenRoute, lang }) {
    let db = data.db || {};
    let t = db.totals || {};
    let [sort, setSort] = useState({ key: 'totalMs', dir: -1 });
    let [openTable, setOpenTable] = useState(null);

    let collections = useMemo(() => {
        let col = COLLECTION_COLUMNS.find((c) => c.key === sort.key);
        return sortRows(db.collections || [], sort.key, sort.dir, col && col.text);
    }, [db.collections, sort]);

    let depsByTable = useMemo(() => {
        let map = {};
        (db.dependencies || []).forEach((d) => { (map[d.collection] = map[d.collection] || []).push(d); });
        Object.keys(map).forEach((k) => { map[k] = sortRows(map[k], 'totalMs'); });
        return map;
    }, [db.dependencies]);

    if (!(db.collections || []).length) {
        return <div style={styles.emptyState}>{'No DB data in this period - only requests logged after the deploy have it.'.translate(lang)}</div>;
    }

    let onSort = (key) => setSort((prev) => (prev.key === key ? { key, dir: -prev.dir } : { key, dir: COLLECTION_COLUMNS.find((c) => c.key === key).text ? 1 : -1 }));

    return (
        <div>
            <div style={styles.kpiRow}>
                <KpiTile label={'Queries'.translate(lang)} value={formatNumber(t.queries)} />
                <KpiTile label={'Time in DB'.translate(lang)} value={ms(t.totalMs)} />
                <KpiTile label={'Documents'.translate(lang)} value={formatNumber(t.docs)} />
                <KpiTile label={'Traffic (est.)'.translate(lang)} value={bytes(t.estimatedBytes)} sub={DB_TRAFFIC_HINT.translate(lang)} />
                <KpiTile label={'Tables'.translate(lang)} value={formatNumber(t.collections)} />
            </div>

            <div style={styles.card}>
                <div style={styles.sectionTitle}>{'Tables'.translate(lang)}</div>
                <div style={{ ...styles.note, marginTop: 0, marginBottom: 10 }}>{'Click a table to see the routes that use it.'.translate(lang)}</div>
                <Table responsive hover size="sm" style={{ fontSize: 13 }}>
                    <thead>
                        <tr>
                            {COLLECTION_COLUMNS.map((c) => (
                                <th key={c.key} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} title={c.title ? c.title.translate(lang) : undefined} onClick={() => onSort(c.key)}>
                                    {c.label.translate(lang)} {sort.key === c.key ? (sort.dir === 1 ? '▲' : '▼') : ''}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {collections.map((c) => {
                            let open = openTable === c.collection;
                            let deps = depsByTable[c.collection] || [];
                            return (
                                <React.Fragment key={c.collection}>
                                    <tr style={{ cursor: 'pointer', backgroundColor: open ? '#F4F6F8' : undefined }} onClick={() => setOpenTable(open ? null : c.collection)}>
                                        <td style={styles.mono}>{open ? '▾ ' : '▸ '}{c.collection}</td>
                                        <td>{formatNumber(c.queries)}</td>
                                        <td>{ms(c.totalMs)}</td>
                                        <td>{ms(c.avgMs)}</td>
                                        <td>{formatNumber(c.docs)}</td>
                                        <td>{bytes(c.docBytes)}</td>
                                        <td>{bytes(c.estimatedBytes)}</td>
                                        <td>{formatNumber(c.requests)}</td>
                                        <td>{formatNumber(c.routes)}</td>
                                    </tr>
                                    {open ? (
                                        <tr style={{ backgroundColor: '#F4F6F8' }}>
                                            <td colSpan={COLLECTION_COLUMNS.length}>
                                                <div style={{ ...styles.sectionTitle, fontSize: 12, marginTop: 6 }}>{'Routes that use it'.translate(lang)}</div>
                                                {deps.length ? (
                                                    <Table size="sm" hover style={{ fontSize: 12, backgroundColor: palette.surface }}>
                                                        <thead>
                                                            <tr>
                                                                <th>{'Method'.translate(lang)}</th>
                                                                <th>{'Route'.translate(lang)}</th>
                                                                <th>{'Queries / request'.translate(lang)}</th>
                                                                <th>{'Total ms'.translate(lang)}</th>
                                                                <th>{'Docs'.translate(lang)}</th>
                                                                <th title={DB_TRAFFIC_HINT.translate(lang)}>{'Traffic (est.)'.translate(lang)}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {deps.map((d) => (
                                                                <tr key={routeKey(d)} style={{ cursor: 'pointer' }} title={'Open in Routes'.translate(lang)} onClick={() => onOpenRoute && onOpenRoute(d)}>
                                                                    <td style={styles.mono}>{d.method}</td>
                                                                    <td style={{ ...styles.mono, wordBreak: 'break-all' }}>{d.route}</td>
                                                                    <td><Flagged severity={dbQueriesSeverity(d.avgQueriesPerRequest)}>{fixed1(d.avgQueriesPerRequest)}</Flagged></td>
                                                                    <td>{ms(d.totalMs)}</td>
                                                                    <td>{formatNumber(d.docs)}</td>
                                                                    <td>{bytes(d.estimatedBytes)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                ) : <div style={styles.note}>{'No route data for this table.'.translate(lang)}</div>}
                                            </td>
                                        </tr>
                                    ) : null}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </Table>
            </div>

            <DependencyMatrix dependencies={db.dependencies} onOpenRoute={onOpenRoute} lang={lang} />
        </div>
    );
}

function CallerTable({ title, rows, filterKey, onOpenLogs, truncate, lang }) {
    return (
        <div style={styles.card}>
            <div style={styles.sectionTitle}>{title}</div>
            <Table hover size="sm" style={{ fontSize: 13, tableLayout: 'fixed', width: '100%' }}>
                <thead>
                    <tr>
                        <th style={{ width: '46%' }}>{'Value'.translate(lang)}</th>
                        <th>{'Requests'.translate(lang)}</th>
                        <th>{'Errors %'.translate(lang)}</th>
                        <th>{'Avg'.translate(lang)}</th>
                    </tr>
                </thead>
                <tbody>
                    {!(rows || []).length ? (
                        <tr><td colSpan={4} style={styles.emptyState}>{'No data'.translate(lang)}</td></tr>
                    ) : rows.map((r, idx) => (
                        <tr key={idx} style={{ cursor: 'pointer' }} title={'Open the logs from the last 24 h'.translate(lang)} onClick={() => onOpenLogs && onOpenLogs({ [filterKey]: r.value })}>
                            <td
                                style={{ ...styles.mono, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: truncate ? 'nowrap' : 'normal', wordBreak: 'break-all' }}
                                title={r.value}
                            >
                                {r.value || '—'}
                            </td>
                            <td>{formatNumber(r.requests)}</td>
                            <td><Flagged severity={errorSeverity(r.errorPercent)}>{pct(r.errorPercent)}</Flagged></td>
                            <td>{ms(r.avgMs)}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
}

/**
* @param {function} onOpenLogs - called with `{username}` / `{ip}` / `{userAgent}`
*/
export function ApiLogsCallersTab({ data, onOpenLogs, lang }) {
    return (
        <div>
            <div style={styles.threeCol}>
                <CallerTable title={'Users'.translate(lang)} rows={data.users} filterKey="username" onOpenLogs={onOpenLogs} lang={lang} />
                <CallerTable title={'IP addresses'.translate(lang)} rows={data.ips} filterKey="ip" onOpenLogs={onOpenLogs} lang={lang} />
                <CallerTable title={'User agents'.translate(lang)} rows={data.userAgents} filterKey="userAgent" onOpenLogs={onOpenLogs} truncate lang={lang} />
            </div>
            <div style={styles.note}>
                {'Top values are kept per hour and per day, so for long periods the numbers for less frequent values are a lower bound. The top callers are accurate.'.translate(lang)}
            </div>
        </div>
    );
}
